import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizeTextareaOptions {
  minHeight?: number;
  maxHeight?: number;
}

interface UseResizeTextareaReturn {
  resizeHandleRef: React.RefObject<HTMLDivElement>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  textareaHeight: number | null;
  handleResizeStart: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void;
}

export const useResizeTextarea = (options: UseResizeTextareaOptions = {}): UseResizeTextareaReturn => {
  const { minHeight = 70, maxHeight = 300 } = options;

  const [textareaHeight, setTextareaHeight] = useState<number | null>(minHeight);
  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const handleResizeMoveRef = useRef<(e: MouseEvent | TouchEvent) => void>(() => { });
  const handleResizeEndRef = useRef<() => void>(() => { });
  const handleTouchMoveRef = useRef<(e: TouchEvent) => void>(() => { });
  const handleTouchEndRef = useRef<() => void>(() => { });

  const getClientY = (e: MouseEvent | TouchEvent): number => {
    if ('touches' in e && e.touches.length > 0) {
      return e.touches[0].clientY;
    }
    return (e as MouseEvent).clientY;
  };

  const handleResizeMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isResizingRef.current || !textareaRef.current) return;

    // Prevent default and stop propagation for touch events to prevent parent page scrolling
    if ('touches' in e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const clientY = getClientY(e);
    const deltaY = startYRef.current - clientY; // Negative delta means moving up (increasing height)
    const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeightRef.current + deltaY));

    setTextareaHeight(newHeight);
    textareaRef.current.style.height = `${newHeight}px`;
  }, [minHeight, maxHeight]);

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    document.removeEventListener('mousemove', handleResizeMoveRef.current);
    document.removeEventListener('mouseup', handleResizeEndRef.current);
    // Remove capture listeners for touch events
    document.removeEventListener('touchmove', handleTouchMoveRef.current, { capture: true } as any);
    document.removeEventListener('touchend', handleTouchEndRef.current, { capture: true } as any);
  }, []);

  // Update refs when handlers change
  handleResizeMoveRef.current = handleResizeMove;
  handleResizeEndRef.current = handleResizeEnd;
  handleTouchMoveRef.current = handleResizeMove;
  handleTouchEndRef.current = handleResizeEnd;

  const handleResizeStart = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent page

    if (!textareaRef.current) return;

    isResizingRef.current = true;

    // Get clientY from either mouse or touch event
    const clientY = 'touches' in e && e.touches.length > 0
      ? e.touches[0].clientY
      : (e as React.MouseEvent<HTMLDivElement>).clientY;

    startYRef.current = clientY;
    startHeightRef.current = textareaRef.current.offsetHeight;

    // Add both mouse and touch event listeners
    // Use capture phase for touch events to catch them before they bubble
    document.addEventListener('mousemove', handleResizeMoveRef.current);
    document.addEventListener('mouseup', handleResizeEndRef.current);
    document.addEventListener('touchmove', handleTouchMoveRef.current, { passive: false, capture: true });
    document.addEventListener('touchend', handleTouchEndRef.current, { capture: true });
  }, [handleResizeMove, handleResizeEnd]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMoveRef.current);
      document.removeEventListener('mouseup', handleResizeEndRef.current);
      // Remove capture listeners for touch events
      document.removeEventListener('touchmove', handleTouchMoveRef.current, { capture: true } as any);
      document.removeEventListener('touchend', handleTouchEndRef.current, { capture: true } as any);
    };
  }, []);

  return {
    resizeHandleRef,
    textareaRef,
    textareaHeight,
    handleResizeStart
  };
};
